import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Calculator, AlertCircle, ChevronRight, Scale, DollarSign, Clock, Mail } from 'lucide-react';
import CompensationEstimator from '../components/CompensationEstimator';
import { CaseValueFactorsGraphic } from '../components/ArticleGraphics';
import SEOHead from '@/components/SEOHead';

const CompensationEstimatorPage = () => {
  // Add HowTo schema for this page
  useEffect(() => {
    const howToSchema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Estimate Your Personal Injury Case Value",
      "description": "Use this free educational tool to get a rough idea of what your personal injury case might be worth in Washington or Alaska.",
      "totalTime": "PT5M",
      "step": [
        {
          "@type": "HowToStep",
          "position": 1,
          "name": "Select Your Injury Type",
          "text": "Choose the type of injury you sustained, such as car accident, slip and fall, maritime injury, or motorcycle crash."
        },
        {
          "@type": "HowToStep",
          "position": 2,
          "name": "Describe Your Injury Severity",
          "text": "Indicate how severe your injuries are - from minor soft tissue injuries to serious conditions requiring surgery or causing permanent disability."
        },
        {
          "@type": "HowToStep",
          "position": 3,
          "name": "Enter Your Medical Expenses",
          "text": "Provide your current and expected medical costs, including hospital bills, therapy, and future treatment."
        },
        {
          "@type": "HowToStep",
          "position": 4,
          "name": "Calculate Lost Wages",
          "text": "Enter income lost due to your injury, including time off work and reduced earning capacity."
        },
        {
          "@type": "HowToStep",
          "position": 5,
          "name": "Assess Fault Clarity",
          "text": "Indicate how clear the fault is in your case - clear fault with evidence, disputed fault, or shared fault."
        },
        {
          "@type": "HowToStep",
          "position": 6,
          "name": "Review Your Estimate",
          "text": "Get your potential compensation range and contact Amy C. Brown for a free consultation for a more accurate case evaluation."
        }
      ]
    };
    
    // Add speakable schema for voice search
    const speakableSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Potential Compensation Estimator | Amy C. Brown Law",
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["h1", ".hero-description", "h2"]
      },
      "url": "https://amycbrownlaw.com/compensation-estimator"
    };
    
    // Add schemas
    const addSchema = (schema, id) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `estimator-${id}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    };
    
    addSchema(howToSchema, 'howto');
    addSchema(speakableSchema, 'speakable');
    
    return () => {
      document.getElementById('estimator-howto')?.remove();
      document.getElementById('estimator-speakable')?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen pt-20" data-testid="compensation-estimator-page">
      <SEOHead 
        title="Free Case Value Calculator | Personal Injury Compensation Estimator"
        description="Estimate your personal injury case value with our free calculator. Get a rough idea of what your car accident, slip and fall, or maritime injury case might be worth in Washington or Alaska."
        pageType="tool"
      />
      {/* Hero Section */}
      <section className="pt-36 pb-12 md:pt-40 md:pb-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Calculator className="w-4 h-4" />
              Free Educational Tool
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
              Potential Compensation Estimator
            </h1>
            <p className="text-xl text-slate-600 mb-4 leading-relaxed">
              Get a Rough Idea of Your Case Value
            </p>
            <p className="text-lg text-slate-500">
              Answer six simple questions to understand what your personal injury case might be worth. This free tool provides educational estimates to help you make informed decisions.
            </p>
          </div>
        </div>
      </section>

      {/* Important Disclaimer */}
      <section className="py-8 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-primary">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-heading text-xl font-semibold text-secondary mb-3">
                  Important Disclaimer
                </h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  This is an educational tool only — every case is unique. The estimates provided are not guarantees and should not be considered legal advice. Contact me directly so we can discuss your specific situation honestly.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="tel:425-675-1224"
                    className="inline-flex items-center gap-2 text-primary font-medium hover:text-coral-600"
                  >
                    <Phone className="w-4 h-4" />
                    (425) 675-1224
                  </a>
                  <a 
                    href="mailto:amy@amycbrownlaw.com"
                    className="inline-flex items-center gap-2 text-primary font-medium hover:text-coral-600"
                  >
                    <Mail className="w-4 h-4" />
                    amy@amycbrownlaw.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Estimator Tool Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <CompensationEstimator isFullPage={true} />
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              How This Estimator Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              I built this tool to help you understand the factors that influence personal injury case values in Washington and Alaska.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-teal-600 font-bold text-2xl">1</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-3">Answer 6 Questions</h3>
              <p className="text-slate-600">
                Simple questions about your injury, medical treatment, and circumstances help me understand your situation.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-teal-600 font-bold text-2xl">2</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-3">Get an Estimate Range</h3>
              <p className="text-slate-600">
                Based on typical case outcomes and the factors you provide, you'll see a potential compensation range.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-teal-600 font-bold text-2xl">3</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-3">Get a Real Evaluation</h3>
              <p className="text-slate-600">
                For an accurate assessment, contact me for a free consultation where I can review your specific case details.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Affects Case Value */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {/* Case Value Factors Graphic */}
          <div className="mb-12">
            <figure>
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-100 p-4 max-w-3xl mx-auto">
                <CaseValueFactorsGraphic />
              </div>
              <figcaption className="text-center text-sm text-slate-500 mt-3 italic">
                Factors that influence your personal injury case value
              </figcaption>
            </figure>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                What Really Affects Your Case Value?
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Every personal injury case is unique. While this estimator gives you a starting point, the actual value of your case depends on many factors that only a detailed review can uncover.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary mb-1">Medical Expenses & Future Care</h4>
                    <p className="text-slate-600 text-sm">Current bills plus projected future treatment costs significantly impact case value.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary mb-1">Lost Wages & Earning Capacity</h4>
                    <p className="text-slate-600 text-sm">Time missed from work and long-term impact on your ability to earn.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Scale className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary mb-1">Insurance Policy Limits</h4>
                    <p className="text-slate-600 text-sm">Available insurance coverage sets practical limits on recoverable amounts.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h3 className="font-heading text-xl font-semibold text-secondary mb-6">Why Online Estimates Have Limits</h3>
              <ul className="space-y-4 text-slate-600">
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span>No tool can account for the specific evidence in your case</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span>Insurance policy limits vary and affect real-world recovery</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span>Jurisdiction, judge, and jury tendencies matter</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span>Negotiation skill significantly impacts settlements</span>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-sm text-slate-500 italic">
                  "I built this tool to be helpful and transparent, but nothing replaces a real conversation about your specific situation."
                </p>
                <p className="text-sm font-semibold text-secondary mt-2">— Amy C. Brown</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl font-bold text-secondary text-center mb-12">
            Related Resources
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Link 
              to="/pricing" 
              className="group bg-slate-50 rounded-xl p-6 hover:bg-teal-50 transition-colors border border-slate-100 hover:border-teal-200"
            >
              <DollarSign className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading text-lg font-semibold text-secondary mb-2 group-hover:text-teal-600 transition-colors">
                How Much Does It Cost?
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                Understand my contingency fee structure — you don't pay unless I win.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm">
                Learn more <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>

            <Link 
              to="/faqs" 
              className="group bg-slate-50 rounded-xl p-6 hover:bg-teal-50 transition-colors border border-slate-100 hover:border-teal-200"
            >
              <AlertCircle className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading text-lg font-semibold text-secondary mb-2 group-hover:text-teal-600 transition-colors">
                FAQs & Common Questions
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                Get answers to questions about the legal process, risks, and what to expect.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm">
                View FAQs <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>

            <Link 
              to="/blog/why-hire-personal-injury-attorney" 
              className="group bg-slate-50 rounded-xl p-6 hover:bg-teal-50 transition-colors border border-slate-100 hover:border-teal-200"
            >
              <Scale className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading text-lg font-semibold text-secondary mb-2 group-hover:text-teal-600 transition-colors">
                Why Hire an Attorney?
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                Studies show represented claimants receive 3.5x more compensation on average.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm">
                Read article <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Ready for a Real Case Evaluation?
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            I'll give you an honest assessment of your case's potential value — no obligation, no pressure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-10 py-5 rounded-full font-bold text-xl hover:bg-teal-50 transition-all shadow-xl"
              data-testid="estimator-page-cta-call"
            >
              <Phone className="w-6 h-6" />
              Call (425) 675-1224
            </a>
            <a
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-6 h-6" />
              Text Me
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompensationEstimatorPage;
